import {
  isVendorArchived,
  partitionVendorsByStatus,
  withNestedStatus,
  getVendorNestedStatus,
} from "../../utilities/vendorsRefresh";

const initial_state = {
  vendors: [],
  active_vendors: [],
  archived_vendors: [],
};

const vendorKey = (vendor) => String(vendor?.POID ?? vendor?.id ?? "");

const rebuildUnion = (active_vendors, archived_vendors) => [
  ...(active_vendors || []),
  ...(archived_vendors || []),
];

const vendorsReducer = (state = initial_state, action) => {
  switch (action.type) {
    case "GET_VENDORS": {
      const list = Array.isArray(action.vendors) ? action.vendors : [];
      const { active_vendors, archived_vendors: fromApi } =
        partitionVendorsByStatus(list);

      // Preserve locally archived vendors if the list API omits them.
      const fromApiIds = new Set(list.map(vendorKey).filter(Boolean));
      const preserved = (state.archived_vendors || []).filter((vendor) => {
        const id = vendorKey(vendor);
        return id && !fromApiIds.has(id) && isVendorArchived(vendor);
      });
      const archived_vendors = [...fromApi, ...preserved];

      return {
        ...state,
        vendors: rebuildUnion(active_vendors, archived_vendors),
        active_vendors,
        archived_vendors,
      };
    }

    case "UPSERT_ACTIVE_VENDOR": {
      const incoming = action.payload;
      if (!incoming) return state;
      const id = vendorKey(incoming);
      if (!id || id === "null") return state;
      const nested = getVendorNestedStatus(incoming);
      const status =
        nested.toLowerCase() === "archived" ? "Active" : nested || "Active";
      const normalized = withNestedStatus(incoming, status);
      const active_vendors = (state.active_vendors || []).some(
        (v) => vendorKey(v) === id
      )
        ? state.active_vendors.map((v) =>
            vendorKey(v) === id ? { ...v, ...normalized } : v
          )
        : [normalized, ...(state.active_vendors || [])];
      const archived_vendors = (state.archived_vendors || []).filter(
        (v) => vendorKey(v) !== id
      );
      return {
        ...state,
        active_vendors,
        archived_vendors,
        vendors: rebuildUnion(active_vendors, archived_vendors),
      };
    }

    case "UPSERT_ARCHIVED_VENDOR": {
      const incoming = action.payload;
      if (!incoming) return state;
      const id = vendorKey(incoming);
      if (!id || id === "null") return state;
      const normalized = withNestedStatus(incoming, "Archived");
      const archived_vendors = (state.archived_vendors || []).some(
        (v) => vendorKey(v) === id
      )
        ? state.archived_vendors.map((v) =>
            vendorKey(v) === id ? { ...v, ...normalized } : v
          )
        : [normalized, ...(state.archived_vendors || [])];
      const active_vendors = (state.active_vendors || []).filter(
        (v) => vendorKey(v) !== id
      );
      return {
        ...state,
        active_vendors,
        archived_vendors,
        vendors: rebuildUnion(active_vendors, archived_vendors),
      };
    }

    case "ARCHIVE_ACTIVE_VENDOR": {
      const incoming = action.payload;
      const id = vendorKey(incoming) || String(action.POID || "");
      if (!id) return state;
      const existing =
        (state.active_vendors || []).find((v) => vendorKey(v) === id) ||
        (state.vendors || []).find((v) => vendorKey(v) === id);
      const normalized = withNestedStatus(
        { ...existing, ...incoming },
        "Archived"
      );
      const active_vendors = (state.active_vendors || []).filter(
        (v) => vendorKey(v) !== id
      );
      const archived_vendors = [
        ...(state.archived_vendors || []).filter((v) => vendorKey(v) !== id),
        normalized,
      ];
      return {
        ...state,
        active_vendors,
        archived_vendors,
        vendors: rebuildUnion(active_vendors, archived_vendors),
      };
    }

    case "REACTIVATE_ARCHIVED_VENDOR": {
      const incoming = action.payload;
      const id = vendorKey(incoming) || String(action.POID || "");
      if (!id) return state;
      const existing =
        (state.archived_vendors || []).find((v) => vendorKey(v) === id) ||
        (state.vendors || []).find((v) => vendorKey(v) === id);
      const normalized = withNestedStatus(
        { ...existing, ...incoming },
        "Active"
      );
      const archived_vendors = (state.archived_vendors || []).filter(
        (v) => vendorKey(v) !== id
      );
      const active_vendors = [
        normalized,
        ...(state.active_vendors || []).filter((v) => vendorKey(v) !== id),
      ];
      return {
        ...state,
        active_vendors,
        archived_vendors,
        vendors: rebuildUnion(active_vendors, archived_vendors),
      };
    }

    case "CLEAR_DATA":
      return { ...initial_state };

    default:
      return state;
  }
};

export default vendorsReducer;
